import { Action } from 'redux'

import ClientPassengerInterface from '../../interfaces/client/Passenger'

import { ADD_PASSENGERS } from '../../actions/passengers/addPassengers'
import { RECEIVE_DELETE_PASSENGERS } from '../../actions/passengers/receiveDeletePassengers'
import { RECEIVE_CHECK_OUT_PASSENGER } from '../../actions/passengers/receiveCheckOutPassenger'
import { RESET_BOOKING } from '../../actions/bookings/resetBooking'

export interface PassengersState {
  byId: {
    [id: string]: ClientPassengerInterface
  }
  allIds: string[]
}

export interface PassengersAction extends Action {
  passengers: ClientPassengerInterface[]
  passengerIds: string[]
  passengerId: string
  flightId: string
}

export interface NormalizePassengers {
  [id: string]: ClientPassengerInterface
}

export const initialState: PassengersState = {
  byId: {},
  allIds: [],
}

export default (
  state = initialState,
  action: PassengersAction = {
    type: '',
    passengers: [],
    passengerIds: [],
    passengerId: '',
    flightId: '',
  }
) => {
  switch (action.type) {
    case ADD_PASSENGERS:
      const currentPassengers = state.allIds.map((id: string) => state.byId[id])

      const allPassengers = [...currentPassengers, ...action.passengers].reduce(
        (
          passengers: NormalizePassengers,
          passenger: ClientPassengerInterface
        ) => {
          const existingPassenger = passengers[passenger.id]

          if (existingPassenger) {
            return {
              ...passengers,
              [passenger.id]: {
                ...existingPassenger,
                ...passenger,
              },
            }
          }

          return {
            ...passengers,
            [passenger.id]: passenger,
          }
        },
        {}
      )

      return {
        ...state,
        byId: allPassengers,
        allIds: Object.keys(allPassengers),
      }

    case RECEIVE_DELETE_PASSENGERS:
      const passengerIdsToBeDeleted = action.passengerIds.filter(
        (passengerId: string) => state.allIds.includes(passengerId)
      )

      const filteredPassengerIds = state.allIds.filter(
        (passengerId: string) => !passengerIdsToBeDeleted.includes(passengerId)
      )

      const filteredPassengersById = { ...state.byId }
      passengerIdsToBeDeleted.forEach(
        (passengerId: string) => delete filteredPassengersById[passengerId]
      )

      // Remove infants
      const infantIdsToBeDeleted = action.passengerIds.filter(
        (passengerId: string) => !state.allIds.includes(passengerId)
      )
      infantIdsToBeDeleted.forEach((infantId: string) => {
        const parent = state.allIds.find((passengerId: string) => {
          const passenger = state.byId[passengerId]
          if (!passenger) return false

          if (passenger.infant && passenger.infant.id) {
            return passenger.infant.id === infantId
          }

          return false
        })

        if (parent && filteredPassengersById[parent]) {
          filteredPassengersById[parent].infant = undefined
        }

        return
      })

      return {
        ...state,
        allIds: filteredPassengerIds,
        byId: filteredPassengersById,
      }

    case RECEIVE_CHECK_OUT_PASSENGER:
      const passenger = state.byId[action.passengerId]

      if (!passenger) {
        return state
      }

      const checkedInOnFlights = passenger.checkedInOnFlights.filter(
        (flightId: string) => flightId !== action.flightId
      )

      return {
        ...state,
        byId: {
          ...state.byId,
          [action.passengerId]: {
            ...passenger,
            checkedInOnFlights,
          },
        },
      }

    case RESET_BOOKING:
    case '@@router/LOCATION_CHANGE':
      return initialState

    default:
      return state
  }
}
